import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { eventServices, EVENT_LOCATIONS } from "../data/services";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal } from "./Reveal";

export const EventsSection = ({ onBook, onOpenDetail }) => {
  const { t, L } = useLanguage();
  const ch = t.chapters.events;

  const handleCardAction = (s) => {
    if (s.packages?.length) {
      onOpenDetail(s);
    } else {
      onBook({
        service: s.name,
        category: "Surprise Event Management",
        locationOptions: EVENT_LOCATIONS,
      });
    }
  };

  return (
    <section id="events" className="py-20 sm:py-28 bg-white" data-testid="events-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <Reveal className="mb-12 sm:mb-16">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <div className="flex items-start gap-5 sm:gap-8">
              <span
                className="font-display text-7xl sm:text-8xl lg:text-9xl leading-none font-semibold text-transparent select-none"
                style={{ WebkitTextStroke: "1.5px #E85D75" }}
              >
                {ch.no}
              </span>
              <div className="max-w-xl pt-2 sm:pt-5">
                <p className="text-xs font-semibold tracking-[0.25em] text-[#E85D75] mb-3 uppercase">
                  {ch.eyebrow}
                </p>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-stone-900">
                  {ch.title}
                </h2>
                <p className="mt-4 text-base text-stone-500 leading-relaxed">{ch.sub}</p>
              </div>
            </div>
            <div
              className="flex items-start gap-3 rounded-2xl bg-[#FAF9F6] border border-stone-200 px-5 py-4 max-w-md"
              data-testid="riverside-note"
            >
              <MapPin className="w-5 h-5 text-[#4A7C59] shrink-0 mt-0.5" />
              <p className="text-sm text-stone-600 leading-relaxed">{t.riversideNote}</p>
            </div>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
          {eventServices.map((s, i) => (
            <Reveal key={s.id} delay={(i % 4) * 0.07} className="h-full">
              <div
                data-testid={`event-card-${s.id}`}
                className="group h-full rounded-3xl overflow-hidden bg-[#FAF9F6] border border-stone-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgb(0,0,0,0.10)] transition-all duration-300 flex flex-col"
              >
                <button
                  onClick={() => handleCardAction(s)}
                  className="h-44 overflow-hidden block w-full"
                  aria-label={s.name}
                >
                  <img
                    src={s.image}
                    alt={s.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </button>
                <div className="p-5 sm:p-6 flex flex-col flex-1">
                  <h3 className="font-display text-xl font-semibold text-stone-900">{L(s, "name")}</h3>
                  <p className="mt-2 text-base text-stone-500 leading-relaxed flex-1">{L(s, "desc")}</p>
                  <div className="mt-4 flex items-center justify-between gap-3">
                    <span className="text-base font-bold text-[#EAB308]" data-testid={`event-price-${s.id}`}>
                      {L(s, "priceLabel") || t.priceOnEnquiry}
                    </span>
                    <motion.button
                      onClick={() => handleCardAction(s)}
                      data-testid={`book-${s.id}`}
                      whileTap={{ scale: 0.95 }}
                      className="rounded-full bg-[#E85D75] text-white text-sm font-semibold px-4 py-2 hover:bg-[#d64a63] transition-colors duration-200 whitespace-nowrap"
                    >
                      {s.packages?.length ? t.viewPackages : "Book Service"}
                    </motion.button>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
